import { useCallback, useState } from 'react'

import { IRange, TYPE_DATE } from 'widgets/tour/date-range-picker'

import { formatDateFetch, prepareDate } from 'shared/lib/calendarDateUtils'

interface UseDateRangeSelectionProps {
  initialSelectedDate: IRange
  difficult?: boolean
  activeDays?: string[]
  triggerCalendar?: (date?: string) => void
  direction?: {
    from?: { cityIataCode?: string }
    to?: { cityIataCode?: string }
  }
}

export const useDateRangeSelection = ({
  initialSelectedDate,
  difficult = false,
  activeDays = [],
  triggerCalendar,
  direction,
}: UseDateRangeSelectionProps) => {
  const [selected, setSelected] = useState<IRange | null>({
    startDate:
      initialSelectedDate.startDate &&
      prepareDate(initialSelectedDate.startDate),
    endDate:
      initialSelectedDate.endDate && prepareDate(initialSelectedDate.endDate),
  })

  const handleDateClick = useCallback(
    (date: string) => {
      const startDateTimestamp = selected?.startDate
        ? new Date(selected.startDate).getTime()
        : null
      const currentDateTimestamp = new Date(date).getTime()

      if (activeDays?.length || difficult) {
        setSelected({ startDate: date })
      } else if (
        selected?.startDate &&
        !selected?.endDate &&
        startDateTimestamp
      ) {
        setSelected((prev) =>
          currentDateTimestamp < startDateTimestamp
            ? { startDate: date, endDate: prev?.startDate }
            : { startDate: prev?.startDate, endDate: date },
        )
      } else {
        if (
          !difficult &&
          triggerCalendar &&
          direction?.from?.cityIataCode &&
          direction?.to?.cityIataCode
        ) {
          triggerCalendar(formatDateFetch(date))
        }
        setSelected({ startDate: date })
      }
    },
    [selected, activeDays, difficult, triggerCalendar, direction],
  )

  const resetValue = useCallback(
    (date: TYPE_DATE) => {
      if (date === TYPE_DATE.START_DATE) {
        setSelected(null)
        if (
          !difficult &&
          triggerCalendar &&
          direction?.from?.cityIataCode &&
          direction?.to?.cityIataCode
        ) {
          triggerCalendar()
        }
      } else {
        setSelected((data) => ({ ...data, endDate: undefined }))
        triggerCalendar?.(formatDateFetch(selected?.startDate) || '')
      }
    },
    [difficult, triggerCalendar, direction, selected?.startDate],
  )

  const updateSelected = useCallback((newSelected: IRange | null) => {
    setSelected(newSelected)
  }, [])

  return {
    selected,
    handleDateClick,
    resetValue,
    updateSelected,
  }
}
